import * as React from 'react';
import * as css from './ContributorsEmailsInput.scss';
import { InjectedTranslateProps, TranslationFunction, Trans } from 'react-i18next';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { EmailErrorType, validateContributorMail } from './contributorValidator';
import { State } from '../../redux/configure-store';
import { TeamMemberData, UserName } from '../../types/teamMembers';
import { Text, MultiSelect, Avatar, TagTheme } from 'wix-style-react';

export type InsertedEmail = {
  email: string;
  id: string;
  errorType?: EmailErrorType;
};

export interface ContributorEmailsInputProps extends InjectedTranslateProps {
  onEmailsAdded?(email: string[], noValidEmail: boolean): void;
  onEmailRemoved?(id: string, noValidEmail: boolean): void;
  teamMembersData: Map<string, TeamMemberData>;
  externalErrorMessage?: string;
  emailInputRef?: HTMLElement;
  emails?: string[];
}

interface ContributorMailsInputState {
  currentValue: string;
  currentHeader: JSX.Element;
  insertedEmails: InsertedEmail[];
}

export class ContributorEmailsComponent extends React.PureComponent<ContributorEmailsInputProps, ContributorMailsInputState> {
  constructor(props) {
    super(props);

    const insertedEmails = props.emails?.map(this.formatEmails) ?? [] ;

    this.state = {
      currentValue: '',
      currentHeader: undefined,
      insertedEmails,
    };

    if (props.onEmailsAdded && insertedEmails.length > 0) {
      props.onEmailsAdded(props.emails, insertedEmails.some(email => email.errorType) );
    }
  }

  createTag(email: string, id: string, errorType: EmailErrorType) {
    const theme: TagTheme = errorType ? 'error' : 'standard';

    if (errorType === EmailErrorType.INVALID_EMAIL) {
      return {
        id,
        label: email,
        theme
      };
    }

    const curUser: any = this.props.teamMembersData.get(email);
    const avatarProps =
      curUser ? { name: this.getFullNameOfUserIfExists(curUser.name), imgProps: { src: curUser.profileImage } } : {};

    return {
      id,
      label: email,
      theme,
      thumb: <Avatar className={css.fixedHeaderAvatar} size="size18" {...avatarProps} />
    };
  }

  formatEmails = (newContributorEmail) => {
    const { teamMembersData } = this.props;
    const { isContributorValid, emailErrorType } = validateContributorMail({
      newContributorEmail,
      existingContributorsEmails: teamMembersData
    });
    return {
      email: newContributorEmail,
      id: newContributorEmail,
      ...(!isContributorValid && { errorType: emailErrorType })
    };
  }

  handleOnAddTag = (newEmails: string[]) => {
    let newInsertedEmails: InsertedEmail[] = [];
    const existingEmails = new Set(this.state.insertedEmails.map(insertedEmail => insertedEmail.email));
    const { onEmailsAdded } = this.props;
    newEmails = newEmails.filter(email => !existingEmails.has(email));

    newInsertedEmails = newEmails.map(this.formatEmails);

    const updatedInsertedEmails = [...this.state.insertedEmails, ...newInsertedEmails];
    this.setState({ insertedEmails: [...this.state.insertedEmails, ...newInsertedEmails], currentValue: '', currentHeader: undefined });
    if (onEmailsAdded) {
      onEmailsAdded(newEmails, updatedInsertedEmails.some(email => email.errorType) );
    }
  }

  handleOnChange = (event) => {
    const newState = { currentValue: event.target.value, currentHeader: undefined };
    const { isContributorValid, emailErrorType } = validateContributorMail({
      newContributorEmail: newState.currentValue,
      existingContributorsEmails: this.props.teamMembersData
    });

    if (isContributorValid) {
      newState.currentHeader = this.getFixedHeader(newState.currentValue);
    } else if (emailErrorType === EmailErrorType.USER_ALREADY_CONTRIBUTOR) {
      const member = this.props.teamMembersData.get(newState.currentValue);
      newState.currentHeader = this.getFixedHeaderAlreadyContributor(member, 'emailsInput.error.userAlreadyContributor');
    } else if (emailErrorType === EmailErrorType.USER_ALREADY_INVITED) {
      const member = this.props.teamMembersData.get(newState.currentValue);
      newState.currentHeader = this.getFixedHeaderAlreadyContributor(member, 'emailsInput.error.userAlreadyInvited');
    }
    this.setState(newState);
  }

  handleRemoveTag = (removedEmailId: string) => {
    const { insertedEmails } = this.state;
    const updatedMailsList = insertedEmails.filter(email => email.id !== removedEmailId);

    this.setState({ insertedEmails: updatedMailsList });
    if (this.props.onEmailRemoved) {
      this.props.onEmailRemoved(removedEmailId, updatedMailsList.some(mail => mail.errorType));
    }
  }

  getTagsFromEmails = () => {
    return this.state.insertedEmails.map((insertedEmail) => (
      this.createTag(insertedEmail.email, insertedEmail.id, insertedEmail.errorType)
    ));
  }

  addTagFromCurrentValue = () => {
    if (this.state.currentValue) {
      this.handleOnAddTag([this.state.currentValue]);
    }
  }

  getFixedHeader(currentValue: string): JSX.Element {
    return (
      <div className={css.fixedHeaderContainer}>
        <Avatar className={css.fixedHeaderAvatar} size="size36"/>
        <div>
          <Trans i18nKey="emailsInput.validEmailHeader.text" values={{ email: currentValue }}>
            <Text className={css.innerHeaderText}>0</Text>
            <Text className={css.innerHeaderText} weight="normal">1</Text>
          </Trans>
        </div>
      </div>
    );
  }

  getFixedHeaderAlreadyContributor(teamMemberData, key) {
    const fullName = this.getFullNameOfUserIfExists(teamMemberData.name);
    const label = fullName.length ? fullName : teamMemberData.email;
    const avatarProps = {
      name: fullName,
      imgProps: { src: teamMemberData.profileImage }
    };
    return (
      <div className={css.fixedHeaderContainer}>
        <Avatar className={css.fixedHeaderAvatar} size="size36" {...avatarProps}/>
        <div>
          <Trans i18nKey={key} values={{ label }}>
            <Text className={css.innerHeaderText} weight="normal">0</Text>
            <Text className={css.innerHeaderText}>1</Text>
          </Trans>
        </div>
      </div>
    );
  }

  getFullNameOfUserIfExists(name: UserName): string {
    if (!name || !(name.firstName + name.lastName)) {
      return '';
    }
    return `${name.firstName} ${name.lastName}`;
  }

  render() {
    const { currentValue, currentHeader, insertedEmails } = this.state;
    const { t, externalErrorMessage } = this.props;
    const invalidMails = insertedEmails.filter(invalidMail => invalidMail.errorType);
    const errorMessage = getDisplayedError(invalidMails, t, externalErrorMessage);

    return (
      <MultiSelect
        fixedHeader={currentHeader}
        autoFocus
        dataHook="contributor-emails-input"
        status={!!invalidMails.length || !!externalErrorMessage ? 'error' : undefined}
        statusMessage={errorMessage}
        value={currentValue}
        onChange={this.handleOnChange}
        tags={this.getTagsFromEmails()}
        onManuallyInput={this.handleOnAddTag}
        onBlur={this.addTagFromCurrentValue}
        onRemoveTag={this.handleRemoveTag}
        clearOnBlur={false}
        highlight={false}
        placeholder={t('emailsInput.placeholder')}
      />
    );
  }
}

const getDisplayedError = (invalidMails: InsertedEmail[], t: TranslationFunction, externalErrorMessage?: string): string => {
  const getDisplayErrorMessage = (mail: InsertedEmail) => {
    switch (mail.errorType) {
      case EmailErrorType.USER_ALREADY_CONTRIBUTOR:
        return t('emailsInput.error.userAlreadyContributor.input', { email: mail.email });
      case EmailErrorType.USER_ALREADY_INVITED:
        return t('emailsInput.error.userAlreadyInvited.input', { email: mail.email });
      case EmailErrorType.INVALID_EMAIL:
      default:
        return t('emailsInput.error.emailInvalid', { email: mail.email });
    }
  };

  if (!!externalErrorMessage) {
    return t(externalErrorMessage);
  }

  return invalidMails.map(getDisplayErrorMessage).join(', ');
};

const mapStateToProps = (state: State) => {
  const teamMembersData = new Map<string, TeamMemberData>();
  state.teamMembers.users.forEach(user => teamMembersData.set(user.email, user));
  state.teamMembers.invites.forEach(invite => teamMembersData.set(invite.email, invite));

  return {
    teamMembersData
  };
};

export default connect(mapStateToProps)(translate()(ContributorEmailsComponent));
